import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { registerNotificationHandler } from '../services/notificationBus';

// Platform Action Types
const PLATFORM_ACTIONS = {
  SET_LOADING: 'SET_LOADING',
  SET_USER: 'SET_USER',
  SET_THEME: 'SET_THEME',
  SET_SIDEBAR_EXPANDED: 'SET_SIDEBAR_EXPANDED',
  SET_ACTIVE_NAV_ITEM: 'SET_ACTIVE_NAV_ITEM',
  SET_MOUSE_POSITION: 'SET_MOUSE_POSITION',
  SET_ERROR: 'SET_ERROR',
  CLEAR_ERROR: 'CLEAR_ERROR',
  SET_NOTIFICATIONS: 'SET_NOTIFICATIONS',
  ADD_NOTIFICATION: 'ADD_NOTIFICATION',
  REMOVE_NOTIFICATION: 'REMOVE_NOTIFICATION'
};

// Initial Platform State
const initialPlatformState = {
  // Authentication & User
  user: null,
  isAuthenticated: false,
  isLoading: false,
  
  // UI State
  theme: 'dark',
  sidebarExpanded: false,
  activeNavItem: null,
  mousePos: { x: 0, y: 0 },
  
  // Error Handling
  error: null,
  
  // Notifications
  notifications: [],
  
  // Platform Settings
  settings: {
    animations: true,
    accessibility: false,
    debugMode: false
  }
};

// Platform Reducer
const platformReducer = (state, action) => {
  switch (action.type) {
    case PLATFORM_ACTIONS.SET_LOADING:
      return { ...state, isLoading: action.payload };
      
    case PLATFORM_ACTIONS.SET_USER:
      return { 
        ...state, 
        user: action.payload,
        isAuthenticated: !!action.payload 
      };
      
    case PLATFORM_ACTIONS.SET_THEME:
      return { ...state, theme: action.payload };
      
    case PLATFORM_ACTIONS.SET_SIDEBAR_EXPANDED:
      return { ...state, sidebarExpanded: action.payload };
      
    case PLATFORM_ACTIONS.SET_ACTIVE_NAV_ITEM:
      return { ...state, activeNavItem: action.payload };
      
    case PLATFORM_ACTIONS.SET_MOUSE_POSITION:
      return { ...state, mousePos: action.payload };
      
    case PLATFORM_ACTIONS.SET_ERROR:
      return { ...state, error: action.payload };
      
    case PLATFORM_ACTIONS.CLEAR_ERROR:
      return { ...state, error: null };
      
    case PLATFORM_ACTIONS.SET_NOTIFICATIONS:
      return { ...state, notifications: action.payload };
      
    case PLATFORM_ACTIONS.ADD_NOTIFICATION:
      return { 
        ...state, 
        notifications: [...state.notifications, action.payload] 
      };
      
    case PLATFORM_ACTIONS.REMOVE_NOTIFICATION:
      return { 
        ...state, 
        notifications: state.notifications.filter(n => n.id !== action.payload) 
      };
      
    default:
      return state;
  }
};

// Create Context
const PlatformContext = createContext();

// Platform Provider Component
export const PlatformProvider = ({ children }) => {
  const [state, dispatch] = useReducer(platformReducer, initialPlatformState);

  // Action Creators
  const actions = {
    setLoading: (loading) => dispatch({ 
      type: PLATFORM_ACTIONS.SET_LOADING, 
      payload: loading 
    }),
    
    setUser: (user) => dispatch({ 
      type: PLATFORM_ACTIONS.SET_USER, 
      payload: user 
    }),
    
    setTheme: (theme) => dispatch({ 
      type: PLATFORM_ACTIONS.SET_THEME, 
      payload: theme 
    }),
    
    setSidebarExpanded: (expanded) => dispatch({ 
      type: PLATFORM_ACTIONS.SET_SIDEBAR_EXPANDED, 
      payload: expanded 
    }),
    
    setActiveNavItem: (item) => dispatch({ 
      type: PLATFORM_ACTIONS.SET_ACTIVE_NAV_ITEM, 
      payload: item 
    }),
    
    setMousePosition: (pos) => dispatch({ 
      type: PLATFORM_ACTIONS.SET_MOUSE_POSITION, 
      payload: pos 
    }),
    
    setError: (error) => dispatch({ 
      type: PLATFORM_ACTIONS.SET_ERROR, 
      payload: error 
    }),
    
    clearError: () => dispatch({ 
      type: PLATFORM_ACTIONS.CLEAR_ERROR 
    }),
    
    addNotification: (notification) => {
      const id = Date.now() + Math.random();
      dispatch({ 
        type: PLATFORM_ACTIONS.ADD_NOTIFICATION, 
        payload: { ...notification, id } 
      });
      return id;
    },
    
    removeNotification: (id) => dispatch({ 
      type: PLATFORM_ACTIONS.REMOVE_NOTIFICATION, 
      payload: id 
    }),

    // Authentication helpers
    login: async (credentials) => {
      actions.setLoading(true);
      try {
        // Mock authentication - replace with real API call
        await new Promise(resolve => setTimeout(resolve, 1000));
        const user = { 
          id: 1, 
          name: 'User', 
          email: credentials.email || 'user@cybpass.ai',
          role: 'analyst'
        };
        actions.setUser(user);
        actions.clearError();
        return user;
      } catch (error) {
        actions.setError('Authentication failed');
        throw error;
      } finally {
        actions.setLoading(false);
      }
    },

    logout: () => {
      actions.setUser(null);
      actions.clearError();
    },

    // UI Helpers
    toggleSidebar: () => {
      actions.setSidebarExpanded(!state.sidebarExpanded);
    }
  };

  // Mouse tracking effect
  useEffect(() => {
    const handleMouseMove = (e) => {
      actions.setMousePosition({ x: e.clientX, y: e.clientY });
    };

    if (state.settings.animations) {
      window.addEventListener('mousemove', handleMouseMove);
      return () => window.removeEventListener('mousemove', handleMouseMove);
    }
  }, [state.settings.animations]);

  // Auto-remove notifications
  useEffect(() => {
    const timeouts = state.notifications.map(notification => {
      if (notification.autoRemove !== false) {
        return setTimeout(() => {
          actions.removeNotification(notification.id);
        }, notification.duration || 5000);
      }
      return null;
    });

    return () => {
      timeouts.forEach(timeout => timeout && clearTimeout(timeout));
    };
  }, [state.notifications]);

  // Bridge non-React modules (axios api client, etc.) to the notification
  // system. Dispatches directly to the reducer so the handler stays valid
  // forever — no stale closures across re-renders.
  useEffect(() => {
    const unregister = registerNotificationHandler((notification) => {
      const id = Date.now() + Math.random();
      dispatch({
        type: PLATFORM_ACTIONS.ADD_NOTIFICATION,
        payload: { ...notification, id }
      });
    });
    return unregister;
  }, []);

  const value = {
    ...state,
    actions
  };

  return (
    <PlatformContext.Provider value={value}>
      {children}
    </PlatformContext.Provider>
  );
};

// Custom Hook
export const usePlatform = () => {
  const context = useContext(PlatformContext);
  if (!context) {
    throw new Error('usePlatform must be used within a PlatformProvider');
  }
  return context;
};

// Export action types for external use
export { PLATFORM_ACTIONS };